import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, Info, Loader2, Minimize2, Package, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import { supabase } from "@/integrations/supabase/client";
import {
  BRAND_COLORS,
  BRAND_DONTS,
  BRAND_DOS,
  BRAND_FONTS,
  BRAND_SPACING,
  CONCEPTS,
  MARKS,
  THEMES,
  buildLogoSvg,
  defaultLogoOptions,
  suggestedBackground,
  type BrandMark,
  type BrandTheme,
  type LogoOptions,
} from "@/lib/brand";
import {
  FORMATS,
  SIZE_PRESETS,
  downloadBlob,
  downloadFaviconPack,
  downloadZip,
  fileName,
  renderBlob,
  svgDataUrl,
  type ExportFormat,
} from "@/lib/brand-export";

export const Route = createFileRoute("/_authenticated/admin/branding")({
  head: () => ({
    meta: [
      { title: "Branding och logotyp | Sellavo" },
      {
        name: "description",
        content:
          "Sellavos logotyp, färger och typsnitt. Ladda ner märket i SVG, PNG, JPG, WebP och ICO.",
      },
      { property: "og:title", content: "Branding – Sellavo" },
      { property: "og:description", content: "Logotyp, färger och typsnitt för Sellavo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: BrandingPage,
});

const BG_CHOICES = [
  { label: "Transparent", value: "transparent" },
  { label: "Mörk", value: "#0c2340" },
  { label: "Ljus", value: "#ffffff" },
];

const CHECKER =
  "linear-gradient(45deg,#25415e 25%,transparent 25%),linear-gradient(-45deg,#25415e 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#25415e 75%),linear-gradient(-45deg,transparent 75%,#25415e 75%)";

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-1.5">
        <h2 className="font-heading text-sm font-bold">{title}</h2>
        {hint && (
          <TouchTooltip text={hint}>
            <span className="text-muted-foreground">
              <Info className="size-3.5" />
            </span>
          </TouchTooltip>
        )}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Preview({
  options,
  className,
  guides = true,
  maxHeight = 280,
}: {
  options: LogoOptions;
  className?: string;
  guides?: boolean;
  /** Högsta höjd i px på ritytan – förhindrar att sidan hoppar vid stora mått. */
  maxHeight?: number;
}) {
  const svg = useMemo(() => buildLogoSvg(options), [options]);
  const padX = `${(options.padding / Math.max(1, options.width)) * 100}%`;
  const padY = `${(options.padding / Math.max(1, options.height)) * 100}%`;
  return (
    <div
      className={`flex items-center justify-center overflow-hidden ${className ?? ""}`}
      style={{ height: maxHeight }}
    >
      <div
        className="relative inline-flex max-h-full max-w-full"
        style={{
          backgroundImage: options.background === "transparent" ? CHECKER : undefined,
          backgroundSize: "16px 16px",
          backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
        }}
      >
        <img
          src={svgDataUrl(svg)}
          alt="Förhandsvisning av logotypen"
          className="block"
          style={{ maxHeight: maxHeight, maxWidth: "100%", width: "auto", height: "auto" }}
        />

        {guides && (
          <div className="pointer-events-none absolute inset-0" aria-hidden>
            <div className="absolute inset-0 border border-primary/70" />
            {options.padding > 0 && (
              <div
                className="absolute border border-dashed border-primary/40"
                style={{ top: padY, bottom: padY, left: padX, right: padX }}
              />
            )}
            <div className="absolute left-0 right-0 top-1/2 border-t border-dashed border-primary/20" />
            <div className="absolute bottom-0 left-1/2 top-0 border-l border-dashed border-primary/20" />
            <span className="absolute bottom-1 right-1 rounded-md bg-background/80 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              {options.width} × {options.height}
              {options.padding === 0 ? " · 0 px marginal" : ` · ${options.padding} px`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}


const SIZE_CHOICES = SIZE_PRESETS.map((s) => ({ width: s.width, height: s.height }));
const BATCH_FORMATS: ExportFormat[] = ["svg", "png"];

const STANDARD_PACK: Record<string, boolean> = {
  "combo|dark|svg": true,
  "combo|dark|png": true,
  "combo|light|svg": true,
  "combo|light|png": true,
  "symbol|dark|svg": true,
  "symbol|dark|png": true,
  "symbol|light|svg": true,
  "symbol|light|png": true,
};

function allKeys(value: boolean) {
  const out: Record<string, boolean> = {};
  for (const m of MARKS)
    for (const t of THEMES) for (const f of BATCH_FORMATS) out[`${m.value}|${t.value}|${f}`] = value;
  return out;
}

function variantOptions(base: LogoOptions, mark: BrandMark, theme: BrandTheme): LogoOptions {
  return { ...base, mark, theme, background: suggestedBackground(theme) };
}

function VariantCard({
  options,
  format,
  label,
  selected,
  onToggle,
  onInspect,
}: {
  options: LogoOptions;
  format: ExportFormat;
  label: string;
  selected: boolean;
  onToggle: () => void;
  onInspect: () => void;
}) {
  const svg = useMemo(() => buildLogoSvg(options), [options]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onPointerDown={() => {
        longPressed.current = false;
        clear();
        timer.current = setTimeout(() => {
          longPressed.current = true;
          onInspect();
        }, 500);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onClick={() => {
        if (longPressed.current) {
          longPressed.current = false;
          return;
        }
        onToggle();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`relative cursor-pointer select-none rounded-lg border p-2 text-left transition-colors ${
        selected ? "border-primary bg-primary/10" : "border-border hover:bg-secondary"
      }`}
    >
      <div
        className="overflow-hidden rounded-md"
        style={{
          backgroundImage: options.background === "transparent" ? CHECKER : undefined,
          backgroundSize: "12px 12px",
          backgroundPosition: "0 0,0 6px,6px -6px,-6px 0",
        }}
      >
        <img
          src={svgDataUrl(svg)}
          alt={`${label} som ${format.toUpperCase()}`}
          loading="lazy"
          className="block h-auto w-full"
          style={{ aspectRatio: `${options.width} / ${options.height}` }}
        />
      </div>
      <p className="mt-2 truncate text-xs font-medium">{label}</p>
      <p className="font-mono text-[10px] text-muted-foreground">
        {format.toUpperCase()} · {options.width} × {options.height}
      </p>

      {selected && (
        <span className="absolute left-1 top-1 rounded-md bg-primary p-0.5 text-primary-foreground">
          <Check className="size-3" />
        </span>
      )}
      <button
        type="button"
        aria-label="Visa detaljer"
        onClick={(e) => {
          e.stopPropagation();
          onInspect();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute right-1 top-1 rounded-md bg-background/80 p-1 text-muted-foreground transition-colors hover:text-foreground"
      >
        <Info className="size-3.5" />
      </button>
    </div>
  );
}

function BrandingPage() {
  const [opts, setOpts] = useState<LogoOptions>(defaultLogoOptions);
  const [format, setFormat] = useState<ExportFormat>("png");
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState<Record<string, boolean>>({});
  const [sizeKeys, setSizeKeys] = useState<Record<string, boolean>>({});
  const [useCurrentSize, setUseCurrentSize] = useState(true);
  const [inspect, setInspect] = useState<{
    options: LogoOptions;
    format: ExportFormat;
    label: string;
  } | null>(null);

  const [presetName, setPresetName] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const [guides, setGuides] = useState(true);
  const qc = useQueryClient();

  const set = (patch: Partial<LogoOptions>) => setOpts((o) => ({ ...o, ...patch }));

  const presets = useQuery({
    queryKey: ["brand-presets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_presets")
        .select("id, name, config, format, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const savePreset = useMutation({
    mutationFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      const { error } = await supabase.from("brand_presets").insert({
        name: presetName.trim(),
        config: JSON.parse(JSON.stringify(opts)),
        format,
        created_by: user.user?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setPresetName("");
      void qc.invalidateQueries({ queryKey: ["brand-presets"] });
      toast.success("Varianten är sparad");
    },
    onError: (e: Error) => toast.error("Kunde inte spara", { description: e.message }),
  });

  const removePreset = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("brand_presets").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["brand-presets"] });
      toast.success("Varianten är borttagen");
    },
  });

  const renamePreset = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase.from("brand_presets").update({ name }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["brand-presets"] }),
  });

  async function download(o: LogoOptions, f: ExportFormat) {
    setBusy(true);
    try {
      const blob = await renderBlob(o, f);
      downloadBlob(blob, fileName(o, f));
      toast.success("Filen laddas ner");
    } catch (e) {
      toast.error("Kunde inte skapa filen", { description: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  const sizes = useMemo(() => {
    const list = SIZE_CHOICES.filter((s) => sizeKeys[`${s.width}x${s.height}`]);
    if (useCurrentSize) list.unshift({ width: opts.width, height: opts.height });
    return list.length ? list : [{ width: opts.width, height: opts.height }];
  }, [sizeKeys, useCurrentSize, opts.width, opts.height]);

  const selectedCount = useMemo(() => Object.values(batch).filter(Boolean).length, [batch]);

  const batchItems = useMemo(() => {
    const items: { options: LogoOptions; format: ExportFormat }[] = [];
    for (const key of Object.keys(batch)) {
      if (!batch[key]) continue;
      const [mark, theme, f] = key.split("|") as [BrandMark, BrandTheme, ExportFormat];
      for (const s of sizes) {
        items.push({
          options: { ...variantOptions(opts, mark, theme), width: s.width, height: s.height },
          format: f,
        });
      }
    }
    return items;
  }, [batch, opts, sizes]);

  async function copy(text: string) {
    await navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied((c) => (c === text ? null : c)), 1500);
  }

  return (
    <AdminShell title="Branding">
      <p className="text-sm text-muted-foreground">
        Sellavos logotyp, färger och typsnitt. Allt ritas som vektor, så exporterna blir skarpa i
        alla storlekar.
      </p>

      <Section
        title="Logotypkoncept"
        hint="Prislappen med S:et är huvudloggan. De tre andra är förslag att titta på och testa."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {CONCEPTS.map((c) => {
            const active = opts.concept === c.value;
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => set({ concept: c.value })}
                aria-pressed={active}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  active ? "border-primary bg-secondary" : "border-border hover:bg-secondary/60"
                }`}
              >
                <div className="rounded-md bg-[#0c2340] p-3">
                  <img
                    src={svgDataUrl(
                      buildLogoSvg({
                        ...defaultLogoOptions(),
                        concept: c.value,
                        background: "transparent",
                        width: 560,
                        height: 160,
                        padding: 12,
                      }),
                    )}
                    alt={c.label}
                    className="h-12 w-full object-contain"
                  />
                </div>
                <p className="mt-2 flex items-center gap-2 text-sm font-semibold">
                  {c.label}
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                      c.official
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-muted-foreground"
                    }`}
                  >
                    {c.official ? "Huvudlogga" : "Förslag"}
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{c.note}</p>
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Export" hint="Välj del, färg, bakgrund, mått och format – ladda sedan ner filen.">
        <Preview options={opts} guides={guides} className="overflow-hidden rounded-lg border border-border" />
        <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox checked={guides} onCheckedChange={(v) => setGuides(!!v)} />
          Visa hjälplinjer (syns bara här, aldrig i filen)
        </label>

        <div className="mt-4 space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Del av loggan
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {MARKS.map((m) => (
                <Button
                  key={m.value}
                  type="button"
                  size="sm"
                  variant={opts.mark === m.value ? "default" : "outline"}
                  onClick={() => set({ mark: m.value })}
                >
                  {m.label}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Färgvariant
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {THEMES.map((t) => (
                <Button
                  key={t.value}
                  type="button"
                  size="sm"
                  variant={opts.theme === t.value ? "default" : "outline"}
                  onClick={() => set({ theme: t.value, background: suggestedBackground(t.value) })}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Bakgrund
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {BG_CHOICES.map((b) => (
                <Button
                  key={b.value}
                  type="button"
                  size="sm"
                  variant={opts.background === b.value ? "default" : "outline"}
                  onClick={() => set({ background: b.value })}
                >
                  {b.label}
                </Button>
              ))}
              <input
                type="color"
                aria-label="Egen bakgrundsfärg"
                value={opts.background === "transparent" ? "#0c2340" : opts.background}
                onChange={(e) => set({ background: e.target.value })}
                className="h-10 w-12 cursor-pointer rounded-md border border-border bg-background p-1"
              />
              <Input
                aria-label="Bakgrund i hex"
                value={opts.background}
                onChange={(e) => set({ background: e.target.value })}
                className="h-10 w-32"
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Storlek
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {SIZE_PRESETS.map((s) => (
                <Button
                  key={s.label}
                  type="button"
                  size="sm"
                  variant={opts.width === s.width && opts.height === s.height ? "default" : "outline"}
                  onClick={() => set({ width: s.width, height: s.height })}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            <div className="mt-2 grid gap-3 sm:grid-cols-4">
              <NumField label="Bredd (px)" value={opts.width} min={16} max={4096} onChange={(n) => set({ width: n })} />
              <NumField label="Höjd (px)" value={opts.height} min={16} max={4096} onChange={(n) => set({ height: n })} />
              <NumField label="Marginal (px)" value={opts.padding} min={0} max={512} onChange={(n) => set({ padding: n })} />
              <NumField label="Rotation (°)" value={opts.rotation} min={-360} max={360} onChange={(n) => set({ rotation: n })} />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <TouchTooltip text="Nollställer marginalen så att logotypen fyller hela ytan">
                <Button
                  type="button"
                  size="sm"
                  variant={opts.padding === 0 ? "default" : "outline"}
                  disabled={opts.padding === 0}
                  onClick={() => set({ padding: 0 })}
                >
                  <Minimize2 className="size-4" /> Minsta marginal
                </Button>
              </TouchTooltip>
              {[0, 90, 180, 270].map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={opts.rotation === d ? "default" : "outline"}
                  onClick={() => set({ rotation: d })}
                >
                  {d}°
                </Button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Filformat
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {FORMATS.map((f) => (
                <Button
                  key={f.value}
                  type="button"
                  size="sm"
                  variant={format === f.value ? "default" : "outline"}
                  onClick={() => setFormat(f.value)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            {format === "ico" && (
              <p className="mt-2 text-xs text-muted-foreground">
                ICO blir alltid kvadratisk och max 256 px – den kortaste sidan avgör storleken.
              </p>
            )}
          </div>

          <Button type="button" disabled={busy} onClick={() => void download(opts, format)} className="w-full">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            Ladda ner {format.toUpperCase()} {opts.width}×{opts.height}
          </Button>
        </div>
      </Section>

      <Section
        title="Flera varianter på en gång"
        hint="Välj vilka storlekar du vill ha och bocka i varianterna. Varje ikryssad variant skapas i varje vald storlek och packas i en zip-fil. Tryck på info-ikonen (eller håll inne på mobil) för att se mått och filnamn."
      >
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Storlekar ({sizes.length} valda)
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {SIZE_CHOICES.map((s) => {
                const key = `${s.width}x${s.height}`;
                const active = sizeKeys[key];
                return (
                  <Button
                    key={key}
                    type="button"
                    size="sm"
                    variant={active ? "default" : "outline"}
                    onClick={() => setSizeKeys((v) => ({ ...v, [key]: !active }))}
                  >
                    {s.width} × {s.height}
                  </Button>
                );
              })}
              <Button
                type="button"
                size="sm"
                variant={useCurrentSize ? "default" : "outline"}
                onClick={() => setUseCurrentSize((v) => !v)}
              >
                Aktuell ({opts.width} × {opts.height})
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setBatch(allKeys(true))}>
              Markera allt
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setBatch({})}>
              Rensa
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setBatch(STANDARD_PACK)}>
              Standardpaket
            </Button>
          </div>

          {MARKS.map((m) => (
            <div key={m.value} className="rounded-lg border border-border p-3">
              <p className="text-sm font-semibold">{m.label}</p>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {THEMES.map((t) =>
                  BATCH_FORMATS.map((f) => {
                    const key = `${m.value}|${t.value}|${f}`;
                    const variant = variantOptions(opts, m.value, t.value);
                    return (
                      <VariantCard
                        key={key}
                        options={variant}
                        format={f}
                        label={t.label}
                        selected={!!batch[key]}
                        onToggle={() => setBatch((b) => ({ ...b, [key]: !b[key] }))}
                        onInspect={() =>
                          setInspect({ options: variant, format: f, label: `${m.label} · ${t.label}` })
                        }
                      />
                    );
                  }),
                )}
              </div>
            </div>
          ))}

          <p className="text-xs text-muted-foreground">
            {batchItems.length
              ? `${batchItems.length} filer · ${selectedCount} varianter × ${sizes.length} storlek${sizes.length === 1 ? "" : "ar"}`
              : "Inga varianter valda än."}
          </p>

          <Button
            type="button"
            variant="outline"
            disabled={busy || batchItems.length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                await downloadZip(batchItems);
                toast.success(`${batchItems.length} filer packas i en zip`);
              } catch (e) {
                toast.error("Kunde inte packa filerna", { description: (e as Error).message });
              } finally {
                setBusy(false);
              }
            }}
            className="w-full"
          >
            <Package className="size-4" /> Ladda ner {batchItems.length || ""} valda som zip
          </Button>
        </div>
      </Section>

      <Dialog open={!!inspect} onOpenChange={(o) => !o && setInspect(null)}>
        <DialogContent className="max-w-lg">
          {inspect && (
            <>
              <DialogHeader>
                <DialogTitle>{inspect.label}</DialogTitle>
                <DialogDescription>Så här ser filen ut och det här är måtten den får.</DialogDescription>
              </DialogHeader>
              <Preview options={inspect.options} guides={false} className="rounded-lg border border-border" />
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <dt className="text-muted-foreground">Format</dt>
                <dd className="font-medium">{inspect.format.toUpperCase()}</dd>
                <dt className="text-muted-foreground">Mått</dt>
                <dd className="font-medium">
                  {inspect.options.width} × {inspect.options.height} px
                </dd>
                <dt className="text-muted-foreground">Marginal</dt>
                <dd className="font-medium">{inspect.options.padding} px</dd>
                <dt className="text-muted-foreground">Rotation</dt>
                <dd className="font-medium">{inspect.options.rotation}°</dd>
                <dt className="text-muted-foreground">Bakgrund</dt>
                <dd className="font-medium">{inspect.options.background}</dd>
                <dt className="text-muted-foreground">Filnamn</dt>
                <dd className="break-all font-mono text-[11px]">
                  {fileName(inspect.options, inspect.format)}
                </dd>
              </dl>
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={busy} onClick={() => void download(inspect.options, inspect.format)}>
                  <Download className="size-4" /> Ladda ner
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setBatch((b) => ({
                      ...b,
                      [`${inspect.options.mark}|${inspect.options.theme}|${inspect.format}`]: true,
                    }));
                    setInspect(null);
                    toast.success("Tillagd i urvalet");
                  }}
                >
                  Lägg till i urvalet
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Section
        title="Favicon och appikoner"
        hint="Ett färdigt paket med alla ikoner en webbplats och PWA behöver – byggt på den symbol och färgvariant du valt ovan."
      >
        <ul className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
          {[
            "favicon.ico (64 px)",
            "favicon.svg (vektor, transparent)",
            "favicon-16x16.png och favicon-32x32.png",
            "apple-touch-icon.png (180 px)",
            "icon-192.png och icon-512.png",
            "icon-512-maskable.png (extra marginal)",
            "og-image.png (1200 × 630)",
          ].map((f) => (
            <li key={f}>· {f}</li>
          ))}
        </ul>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          className="mt-3 w-full"
          onClick={async () => {
            setBusy(true);
            try {
              await downloadFaviconPack(opts);
              toast.success("Ikonpaketet laddas ner");
            } catch (e) {
              toast.error("Kunde inte skapa paketet", { description: (e as Error).message });
            } finally {
              setBusy(false);
            }
          }}
        >
          <Package className="size-4" /> Ladda ner ikonpaket som zip
        </Button>
      </Section>

      <Section
        title="Sparade varianter"
        hint="Spara en inställning med namn så kan alla administratörer ladda ner exakt samma fil igen."
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-48 flex-1">
            <Label htmlFor="preset-name">Namn</Label>
            <Input
              id="preset-name"
              value={presetName}
              placeholder="T.ex. Instagram-profil 1024"
              onChange={(e) => setPresetName(e.target.value)}
              className="mt-1"
            />
          </div>
          <Button
            type="button"
            disabled={!presetName.trim() || savePreset.isPending}
            onClick={() => savePreset.mutate()}
          >
            {savePreset.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Spara nuvarande
          </Button>
        </div>

        <ul className="mt-4 space-y-2">
          {presets.data?.length === 0 && (
            <li className="text-sm text-muted-foreground">Inga sparade varianter än.</li>
          )}
          {presets.data?.map((p) => {
            const cfg = p.config as unknown as LogoOptions;
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-3">
                <div className="min-w-40 flex-1">
                  <Input
                    aria-label="Namn på variant"
                    defaultValue={p.name}
                    onBlur={(e) => {
                      const name = e.target.value.trim();
                      if (name && name !== p.name) renamePreset.mutate({ id: p.id, name });
                    }}
                    className="h-8"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {cfg.mark} · {cfg.theme} · {cfg.width}×{cfg.height} · {String(p.format).toUpperCase()}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setOpts(cfg);
                    setFormat(p.format as ExportFormat);
                  }}
                >
                  Öppna
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  aria-label="Ladda ner variant"
                  onClick={() => void download(cfg, p.format as ExportFormat)}
                >
                  <Download className="size-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  aria-label="Ta bort variant"
                  onClick={() => removePreset.mutate(p.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section title="Färger" hint="Klicka på kopiera-knappen för att ta med hex-koden.">
        <ul className="grid gap-2 sm:grid-cols-2">
          {BRAND_COLORS.map((c) => (
            <li key={c.name} className="flex items-center gap-3 rounded-lg border border-border p-3">
              <span
                className="size-9 shrink-0 rounded-md border border-border"
                style={{ backgroundColor: c.hex }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{c.name}</p>
                <p className="truncate text-xs text-muted-foreground">{c.use}</p>
                <p className="mt-1 font-mono text-xs text-muted-foreground">
                  {c.hex} · {c.token}
                </p>
              </div>
              <Button
                type="button"
                size="icon"
                variant="outline"
                aria-label={`Kopiera ${c.hex}`}
                onClick={() => void copy(c.hex)}
              >
                {copied === c.hex ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Typsnitt">
        <ul className="space-y-3">
          {BRAND_FONTS.map((f) => (
            <li key={f.role} className="rounded-lg border border-border p-3">
              <p className="text-sm font-semibold">{f.role}</p>
              <p className="text-xs text-muted-foreground">
                {f.name} · {f.weights}
              </p>
              <p className="mt-2 text-2xl font-extrabold tracking-tight" style={{ fontFamily: f.css }}>
                {f.sample}
              </p>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Fri yta och minsta storlek">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border p-3">
            <div className="rounded-md bg-[#0c2340] p-6">
              <div className="relative mx-auto max-w-xs border border-dashed border-primary/40 p-[12%]">
                <img
                  src={svgDataUrl(
                    buildLogoSvg({
                      ...defaultLogoOptions(),
                      concept: opts.concept,
                      background: "transparent",
                      width: 560,
                      height: 160,
                      padding: 0,
                    }),
                  )}
                  alt="Fri yta runt logotypen"
                  className="w-full"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Fri yta: minst {BRAND_SPACING.clearSpaceRatio * 100} % av symbolens höjd runt om.
            </p>
          </div>
          <div className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
            <p className="font-semibold text-foreground">Minsta storlek</p>
            <p className="mt-1">Symbol: {BRAND_SPACING.minSymbolPx} px hög.</p>
            <p>Kombination: {BRAND_SPACING.minComboPx} px bred.</p>
            <p className="mt-3">
              Under de måtten tappar S:et i prislappen läsbarhet – använd symbolen ensam i stället.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Gör och gör inte">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border p-3">
            <p className="text-sm font-semibold text-foreground">Gör</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {BRAND_DOS.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-destructive/40 p-3">
            <p className="text-sm font-semibold text-foreground">Gör inte</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {BRAND_DONTS.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>
    </AdminShell>
  );
}

function NumField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    const n = Number(draft);
    setDraft(null);
    if (draft.trim() === "" || Number.isNaN(n)) return;
    onChange(Math.min(max, Math.max(min, Math.round(n))));
  };

  return (
    <label className="block text-sm">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <Input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft ?? value}
        onChange={(e) => setDraft(e.target.value)}

        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
        className="mt-1 h-10"
      />
    </label>
  );
}
